/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { SerializedEditorState } from 'lexical';
import { Option, ValueListPlain } from '@g17eco/types/valueList';
import { ColumnType, ColumnValueAggregation, UtrValueType, ValueAggregation } from '@g17eco/core';

/**
 * @deprecated Use UtrValueType and ColumnType from '@g17eco/core' instead.
 */
export { UtrValueType, ColumnType as TableColumnType } from '@g17eco/core';

export type AggregationValueType = UtrValueType.Table | UtrValueType.Percentage | UtrValueType.Number;

export enum UniversalTrackerType {
  Utr = 'utr',
}

export type UniversalTrackerBase = Pick<
  UniversalTrackerPlain,
  | '_id'
  | 'code'
  | 'typeCode'
  | 'name'
  | 'description'
  | 'alternatives'
  | 'type'
  | 'valueLabel'
  | 'numberScale'
  | 'tags'
  | 'unit'
  | 'instructions'
  | 'instructionsEditorState'
  | 'unitType'
  | 'valueValidation'
  | 'typeTags'
  | 'targetDirection'
  | 'numberScaleInput'
  | 'instructionsLink'
  | 'evidenceInstructions'
  | 'valueType'
  | 'aggregationValueType'
> & {
  created?: string;
};

export type AlternativeProps = Pick<
  UniversalTrackerBase,
  'name' | 'description' | 'valueLabel' | 'instructions' | 'evidenceInstructions' | 'instructionsLink' | 'typeCode'
>;

export const hasMinAndMaxColumnType = (
  columnType: ColumnType,
  min: number | undefined,
  max: number | undefined,
) => {
  switch (columnType) {
    case ColumnType.Number:
      return min === 0 && max === 100;
    case ColumnType.Percentage:
      return true;
    default:
      return false;
  }
};

export const getTableColumnTypeNames = (type: ColumnType, hasListId: boolean = false, col?: TableColumn) => {
  const names = {
    [ColumnType.Number]: 'Number',
    [ColumnType.Text]: 'Text',
    [ColumnType.Date]: 'Date',
    [ColumnType.Percentage]: 'Number',
    [ColumnType.ValueListMulti]: 'Text Multiple-Choices',
    [ColumnType.ValueList]: 'Text Multiple-Choice',
  };
  if (type === ColumnType.Text && hasListId) {
    return names[ColumnType.ValueList];
  }
  if (hasMinAndMaxColumnType(type, col?.validation?.min, col?.validation?.max)) {
    return 'Percentage';
  }
  return names[type];
};

export interface UniversalTrackerMin {
  _id: string;
  name: string;
  type: string;
  valueType?: string;
}

export interface Tags {
  cdsb?: string[];
  tcfd?: string[];
  pic?: string[];
  ese?: string[];
  sdg?: string[];
  wef?: string[];
  ungc?: string[];
  sec?: string[];
  see?: string[];
  /** @deprecated replaced with eesg_2024 */
  esg30?: string[];
  eesg_2024?: string[];
}

export interface UniversalTrackerBlueprintMin extends UniversalTrackerMin {
  _id: string;
  code: string;
  typeCode: string;
  valueLabel: string;
  alternatives?: { [key: string]: Alternative | LanguageAlternative };
  tags?: Tags;
  valueValidation?: ValueValidation;
  instructions?: string;
  valueType: string;
  typeTags?: string[];
  unit?: string;
  unitType?: string;
  numberScale?: string;
  ownerId?: string;
  valueAggregation?: ValueAggregation;
  aggregationConfig?: UtrAggregationConfig;
  aggregationValueType?: AggregationValueType;
}

export type BulkActionUtrMin = Partial<
  Pick<UniversalTrackerBlueprintMin, 'name' | 'valueValidation' | 'unit' | 'unitType' | 'valueType' | 'numberScale' | 'valueAggregation' | 'aggregationConfig' | 'ownerId' | 'aggregationValueType'>
>;

export interface Alternative {
  name: string;
  valueLabel: string;
  alternativeType?: string;
  languageCode?: string;
  typeCode?: string;
  description?: string;
  instructions?: string;
  instructionsEditorState?: SerializedEditorState;
  instructionsLink?: string;
  evidenceInstructions?: string;
  sampleSizeLabel?: string;
  typeTags?: string[];
}

export interface LanguageAlternative extends Alternative {
  alternativeType: 'language';
  languageCode: string;
}

export enum VariationDataSource {
  LastMonth = 'last_month',
  LastYear = 'last_year',
  LatestVerified = 'latest_verified',
}

export enum VariationType {
  Percentage = 'percentage',
}

export interface Variation {
  type: VariationType;
  min: number;
  max: number;
  dataSource: VariationDataSource;
  confirmationRequired: boolean;
}

export interface ValueValidation {
  min?: number;
  max?: number;
  decimal?: number;
  variations?: Variation[];
  valueList?: {
    list?: Option[];
    type: 'list' | 'custom';
    listId?: string;
    custom: any;
    allowCustomOptions?: boolean;
  };
  table?: ValueTable;
}

interface Calculation {
  formula: string;
  useDisaggregations?: boolean;
  variables: {
    [key: string]: CalculationVariable;
  };
}

export enum ConditionVisibility {
  Disabled = 'disabled',
  Hidden = 'hidden',
}

interface Condition {
  code: string;
  calculation: Calculation;
  result: {
    visibility: ConditionVisibility;
    userMessage?: string;
  };
}

export interface UniversalTrackerPlain extends UniversalTrackerMin {
  _id: string;
  instructions?: string;
  instructionsEditorState?: SerializedEditorState;
  code: string;
  created: string;
  valueListOrdered?: any[];
  valueListTargets?: any[];
  description?: string;
  type: string;
  typeCode: string;
  typeTags?: string[];
  targetDirection?: string;
  valueLabel: string;
  valueType: string;
  name: string;
  ownerId?: string;
  unitType?: string;
  unit?: string;
  unitInput?: string;
  numberScaleInput?: string;
  unitLocked?: boolean;
  numberScaleLocked?: boolean;
  numberScale?: string;
  valueValidation?: ValueValidation;
  alternatives?: { [key: string]: Alternative | LanguageAlternative };
  tags?: Tags;
  valueListOptions?: ValueListPlain;
  tableColumnValueListOptions?: ValueListPlain[];
  calculation?: Calculation;
  conditions?: Condition[];
  instructionsLink?: string;
  evidenceInstructions?: string;
  valueAggregation?: ValueAggregation;
  aggregationConfig?: UtrAggregationConfig;
  aggregationValueType?: AggregationValueType;
}

export interface CalculationVariable {
  code: string;
  valueAggregation?: string | null;
  valueListCode?: string;
}

export interface UniveresalTrackerGroup {
  _id: string;
  code: string;
  name: string;
  initiativeId: string;
  universalTrackers: string[];
}

export interface EvidenceLink {
  url: string;
  description?: string;
}

export enum UtrType {
  Calculation = 'calculation',
  Kpi = 'kpi',
  CustomKpi = 'custom_kpi',
  SdgComponent = 'sdg-component',
  Generated = 'generated',
}

/**
 * Allow to group using list of columns
 */
export interface TableGroupColumn {
  /** Column code to group by **/
  code: string;
}

export enum TableAggregationType {
  Group = 'group',
}

export interface TableAggregation {
  type: TableAggregationType.Group;
  columns: TableGroupColumn[];
}

export interface ValueTable {
  validation?: {
    maxRows: number;
  };
  columns: TableColumn[];
  aggregation?: TableAggregation;
  /** Column code that writes back to the parent `.value` field after aggregation */
  valueColumn?: string;
}

export interface TableColumn {
  type: ColumnType;
  code: string;
  name: string;
  shortName?: string;
  options?: Option[];
  unit?: string;
  listId?: string;
  unitType?: string;
  numberScale?: string;
  unitInput?: string;
  numberScaleInput?: string;
  unitLocked?: boolean;
  numberScaleLocked?: boolean;
  instructions?: string;
  calculation?: {
    formula: string;
  };
  visibilityRule?: {
    formula: string;
  };
  disabledRule?: {
    formula: string;
  };
  validation?: {
    max?: number;
    min?: number;
    decimal?: number;
    variations?: Variation[];
    required?: boolean;
    allowCustomOptions?: boolean;
  };
  valueAggregation?: ColumnValueAggregation;
  aggregationConfig?: ColumnAggregationConfig;
}

/**
 * Core interface for aggregation mode configuration
 * Contains common fields used across all aggregation contexts
 */
export interface AggregationModeConfig<T extends ColumnValueAggregation | ValueAggregation | string> {
  valueAggregation: T;
  weightFormula?: string;
}

/**
 * Base aggregation configuration interface
 * Uses enum keys for better type safety
 */
export interface BaseAggregationConfig<T extends ColumnValueAggregation | ValueAggregation | string> {
  modes?: {
    children?: AggregationModeConfig<T>;
    combined?: AggregationModeConfig<T>;
  };
}

/**
 * UTR-specific aggregation configuration
 * Constrains valueAggregation to UTR-level aggregations only
 */
export type UtrAggregationConfig = BaseAggregationConfig<ValueAggregation> & {
  table?: {
    aggregation: TableAggregation;
  };
};

/**
 * Column-specific aggregation configuration
 * Constrains valueAggregation to column-level aggregations only
 */
export type ColumnAggregationConfig = BaseAggregationConfig<ColumnValueAggregation>;
